import type {
  MentionQuery,
  MentionSummary,
  OverviewAttention,
  OverviewAttentionLine,
  OverviewAttentionRule
} from "@/shared/api/api-client";
import {
  CRITERIA_SCHEMA_VERSION,
  NO_VALUE,
  type MentionFilterCriteria
} from "@/features/mentions/mention-filter-criteria";
import { localInstant } from "@/shared/utils/date-range";
import { formatDecimal, formatInteger } from "@/shared/utils/format";

/**
 * As linhas do painel "Pontos de atenção" da Visão Geral (2026-11-01, de
 * três pra quinze em 2026-11-02): o que cada uma mede, quando acende, o
 * texto da linha e pra onde ela leva.
 *
 * **O recorte é a definição.** Quase toda linha é o recorte de um filtro da
 * tela de menções (`MentionFilterCriteria`). O MESMO recorte vira a
 * contagem (`GET /mentions/count`, por `countQuery`) e o link que abre a
 * lista já filtrada (`?filtro=`), então "12 negativas no Tier 1" abre
 * exatamente essas 12. Nenhuma das linhas usa catálogo por id (marca,
 * subtema…), e é isso que deixa a tradução recorte → consulta caber aqui
 * em vez de passar pelo hook da tela de menções.
 *
 * O vocabulário, as faixas e os defaults são do servidor
 * (`web_api/application/overview_attention.py`); os de cá são a cópia que
 * a tela usa enquanto o ajuste não chega, e o teste de render os confere.
 */

export type AttentionUnit = "count" | "percent" | "cena" | "points" | "days";
export type AttentionGroup = "reputation" | "classification" | "routine";

export type AttentionLineMeta = {
  key: OverviewAttentionLine;
  /** O nome da linha no editor. */
  label: string;
  group: AttentionGroup;
  unit: AttentionUnit;
  /** O rótulo do limite no editor, e o que vem depois do campo. */
  thresholdLabel: string;
  suffix?: string;
  min: number;
  max: number;
  step: number;
  hint?: string;
  defaults: OverviewAttentionRule;
};

export const ATTENTION_GROUPS: Array<{ key: AttentionGroup; label: string }> = [
  { key: "reputation", label: "Reputação" },
  { key: "classification", label: "Classificação" },
  { key: "routine", label: "Dados e rotina" }
];

const MAX_COUNT = 100_000;
const MAX_POINTS = 10_000_000;

function countLine(
  key: OverviewAttentionLine,
  label: string,
  group: AttentionGroup,
  options: { enabled?: boolean; hint?: string } = {}
): AttentionLineMeta {
  return {
    key,
    label,
    group,
    unit: "count",
    thresholdLabel: "Alertar a partir de",
    min: 1,
    max: MAX_COUNT,
    step: 1,
    hint: options.hint,
    defaults: { enabled: options.enabled ?? false, threshold: 1 }
  };
}

/** Na ordem do painel. As três de antes nascem ligadas; as doze de
 *  2026-11-02, desligadas — o painel de nenhuma empresa muda sozinho. */
export const ATTENTION_LINES: AttentionLineMeta[] = [
  countLine("negative_mentions", "Menções negativas", "reputation", { enabled: true }),
  {
    key: "negative_share",
    label: "Proporção de negativas",
    group: "reputation",
    unit: "percent",
    thresholdLabel: "Alertar a partir de",
    suffix: "%",
    min: 1,
    max: 100,
    step: 1,
    hint: "Sobre as menções classificadas da janela.",
    defaults: { enabled: false, threshold: 30 }
  },
  countLine("negative_title_citation", "Negativas com a marca no título", "reputation", {
    hint: "Só imprensa."
  }),
  countLine("negative_top_tier", "Negativas em veículo Tier 1 ou Supertier", "reputation", {
    hint: "Só imprensa."
  }),
  {
    key: "low_cena",
    label: "CENA médio",
    group: "reputation",
    unit: "cena",
    thresholdLabel: "Alertar abaixo de",
    min: 0,
    max: 10,
    step: 0.1,
    hint: "Média das menções de imprensa da janela.",
    defaults: { enabled: false, threshold: 5 }
  },
  {
    key: "low_cena_balance",
    label: "Saldo reputacional",
    group: "reputation",
    unit: "points",
    thresholdLabel: "Alertar abaixo de",
    min: -MAX_POINTS,
    max: MAX_POINTS,
    step: 1,
    hint: "Soma do ESCena das menções com nota. O limite pode ser negativo.",
    defaults: { enabled: false, threshold: 0 }
  },
  countLine("unclassified_mentions", "Menções sem regra", "classification", { enabled: true }),
  countLine("neutral_final", "Menções com NEUTRO final", "classification", {
    hint: "Na escala de 4 níveis, NEUTRO final é sinal de que a classificação não decidiu."
  }),
  countLine("unreviewed_mentions", "Menções a revisar", "classification"),
  countLine("no_key_message", "Menções sem mensagem-chave", "classification"),
  countLine("no_theme_highlight", "Menções sem subtema em destaque", "classification", {
    hint: "Sem subtema, ou com empate que ninguém desfez."
  }),
  countLine("shared_post", "Posts em mais de um cliente", "classification"),
  countLine("upload_issues", "Uploads com pendências", "routine", {
    enabled: true,
    hint: "Desligada, some também o aviso de uploads em processamento."
  }),
  {
    key: "stale_data",
    label: "Dias sem dado novo",
    group: "routine",
    unit: "days",
    thresholdLabel: "Alertar a partir de",
    suffix: "dias",
    min: 1,
    max: 365,
    step: 1,
    hint: "Conta a partir do dia da menção mais recente.",
    defaults: { enabled: false, threshold: 3 }
  },
  {
    key: "expiring_drafts",
    label: "Rascunhos de amostra perto de vencer",
    group: "routine",
    unit: "days",
    thresholdLabel: "Avisar com",
    suffix: "dias de antecedência",
    min: 1,
    max: 7,
    step: 1,
    hint: "O rascunho vence 7 dias depois de gerado.",
    defaults: { enabled: false, threshold: 2 }
  }
];

export const DEFAULT_OVERVIEW_ATTENTION = Object.fromEntries(
  ATTENTION_LINES.map(line => [line.key, { ...line.defaults }])
) as OverviewAttention;

/** As linhas contadas por `GET /mentions/count`. */
export const COUNTED_LINES: OverviewAttentionLine[] = [
  "negative_title_citation",
  "negative_top_tier",
  "neutral_final",
  "unreviewed_mentions",
  "no_key_message",
  "no_theme_highlight",
  "shared_post"
];

/** O que decide se uma linha FAZ SENTIDO pra esta empresa e esta pessoa:
 *  as do CENA somem com o CENA oculto, o NEUTRO final só existe na escala
 *  de 4 níveis, e os rascunhos de amostra são de Técnico pra cima (é a
 *  trava da rota que os lista). */
export type AttentionApplicability = {
  showsCena: boolean;
  polarityScale: "four_level" | "three_level";
  draftsVisible: boolean;
};

export function lineApplies(key: OverviewAttentionLine, context: AttentionApplicability): boolean {
  if (key === "low_cena" || key === "low_cena_balance") return context.showsCena;
  if (key === "neutral_final") return context.polarityScale === "four_level";
  if (key === "expiring_drafts") return context.draftsVisible;
  return true;
}

/** O ajuste completo: o que veio do servidor sobre o default, pra uma
 *  resposta velha (de antes de uma linha existir) não quebrar a tela. */
export function completeAttention(stored: Partial<OverviewAttention> | null | undefined): OverviewAttention {
  return { ...DEFAULT_OVERVIEW_ATTENTION, ...(stored ?? {}) };
}

// --- o recorte de cada linha ---------------------------------------------

/** O que o recorte precisa saber da janela e do domínio de polaridade —
 *  tudo vem do resumo, que a Visão Geral já carrega. */
export type AttentionWindow = {
  firstDay: string;
  lastDay: string;
  negativeNames: string[];
  neutralNames: string[];
};

export function attentionWindow(summary: MentionSummary | null): AttentionWindow | null {
  if (!summary?.first_day || !summary.last_day) return null;
  const counts = summary.polarity_counts ?? [];
  return {
    firstDay: summary.first_day,
    lastDay: summary.last_day,
    negativeNames: counts.filter(entry => entry.tone === "negative").map(entry => entry.name),
    neutralNames: counts.filter(entry => entry.tone === "neutral").map(entry => entry.name)
  };
}

/** O recorte da tela de menções que a linha conta e abre, ou `null` pras
 *  linhas que não são recorte de menções (uploads, CENA, dados, rascunhos). */
export function lineCriteria(
  key: OverviewAttentionLine,
  window: AttentionWindow
): MentionFilterCriteria | null {
  const base: MentionFilterCriteria = {
    schema_version: CRITERIA_SCHEMA_VERSION,
    date_from: window.firstDay,
    date_to: window.lastDay
  };
  const negatives = { polarities: { values: window.negativeNames } };
  // Tier e citação no título são de MEIO-ESCOPO: recortam a imprensa e
  // deixam a rede passar inteira. Sem a fonte, "negativas no Tier 1"
  // contaria toda negativa de rede junto.
  const press = { sources: { values: ["press"] } };
  switch (key) {
    case "negative_mentions":
    case "negative_share":
      return { ...base, facets: negatives };
    case "negative_title_citation":
      return { ...base, facets: { ...negatives, ...press }, title_citation: true };
    case "negative_top_tier":
      return { ...base, facets: { ...negatives, ...press, tiers: { values: ["supertier", "tier1"] } } };
    case "unclassified_mentions":
      return { ...base, facets: { polarities: { values: [NO_VALUE] } } };
    case "neutral_final":
      return { ...base, facets: { polarities: { values: window.neutralNames } } };
    case "unreviewed_mentions":
      return { ...base, reviewed: false };
    case "no_key_message":
      return { ...base, facets: { keyMessages: { values: [NO_VALUE] } } };
    case "no_theme_highlight":
      return { ...base, theme_highlight: false };
    case "shared_post":
      return { ...base, shared_post: true };
    default:
      return null;
  }
}

/** O recorte como consulta de `GET /mentions/count` — só os campos que as
 *  linhas usam, com a MESMA conversão de data da tela de menções
 *  (`localInstant`), pra contagem e lista concordarem. */
export function countQuery(criteria: MentionFilterCriteria): MentionQuery {
  const facets = criteria.facets ?? {};
  const values = (facet: { values?: string[] } | undefined) =>
    facet?.values?.length ? facet.values : undefined;
  return {
    polarities: values(facets.polarities),
    postSources: values(facets.sources),
    tiers: values(facets.tiers),
    noKeyMessage: facets.keyMessages?.values?.includes(NO_VALUE) || undefined,
    reviewed: criteria.reviewed,
    titleCitation: criteria.title_citation,
    themeHighlight: criteria.theme_highlight,
    sharedPost: criteria.shared_post,
    dateFrom: criteria.date_from ? localInstant(criteria.date_from, "", "start") : undefined,
    dateTo: criteria.date_to ? localInstant(criteria.date_to, "", "end") : undefined
  };
}

// --- a linha pronta pra tela ----------------------------------------------

/** O número de cada linha contada: `number` = contou, `null` = a contagem
 *  falhou, ausente = ainda contando. */
export type AttentionCounts = Partial<Record<OverviewAttentionLine, number | null>>;

export type AttentionInputs = {
  attention: OverviewAttention;
  summary: MentionSummary | null;
  pendingUploads: number;
  uploadIssues: number;
  counts: AttentionCounts;
  /** Quando vence cada rascunho pendente; `null` = a leitura falhou,
   *  ausente = ainda lendo. */
  draftExpiries?: string[] | null;
  /** "AAAA-MM-DD" de hoje no relógio da instalação, e o instante agora. */
  today: string;
  now: number;
};

export type AttentionTarget =
  | { kind: "mentions"; criteria: MentionFilterCriteria | null }
  | { kind: "uploads" | "dashboard" | "drafts" };

export type AttentionRow = {
  key: OverviewAttentionLine;
  lit: boolean;
  /** `busy` = uploads em processamento (o ícone de carga, sem alerta). */
  busy: boolean;
  title: string;
  detail: string;
  target: AttentionTarget;
};

type CountCopy = { noun: string; action: string; none: string };

const COUNT_COPY: Partial<Record<OverviewAttentionLine, CountCopy>> = {
  negative_mentions: {
    noun: "menções negativas",
    action: "Revisar polaridade e abordagem",
    none: "Nenhuma revisão crítica pendente"
  },
  negative_title_citation: {
    noun: "negativas com a marca no título",
    action: "Matérias de imprensa que citam a marca",
    none: "Nenhuma negativa cita a marca no título"
  },
  negative_top_tier: {
    noun: "negativas em Tier 1 ou Supertier",
    action: "Matérias nos veículos de maior peso",
    none: "Nenhuma negativa em Tier 1 ou Supertier"
  },
  unclassified_mentions: {
    noun: "menções sem regra",
    action: "Classificação pendente",
    none: "Todas as menções estão classificadas"
  },
  neutral_final: {
    noun: "menções com NEUTRO final",
    action: "Conferir a classificação de sentimento",
    none: "Nenhum NEUTRO final"
  },
  unreviewed_mentions: {
    noun: "menções a revisar",
    action: "Revisar e marcar como revisadas",
    none: "Todas as menções foram revisadas"
  },
  no_key_message: {
    noun: "menções sem mensagem-chave",
    action: "Atribuir a mensagem-chave",
    none: "Todas têm mensagem-chave"
  },
  no_theme_highlight: {
    noun: "menções sem subtema em destaque",
    action: "Escolher o subtema em destaque",
    none: "Todas têm subtema em destaque"
  },
  shared_post: {
    noun: "posts em mais de um cliente",
    action: "Conferir o vínculo entre os clientes",
    none: "Nenhum post em mais de um cliente"
  }
};

const DAY_MS = 24 * 60 * 60 * 1000;

function dayNumber(isoDay: string): number {
  const [year, month, day] = isoDay.slice(0, 10).split("-").map(Number);
  return Date.UTC(year, month - 1, day) / DAY_MS;
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

function belowLimit(text: string): string {
  return `Abaixo do limite de alerta (${text})`;
}

/** Cada linha LIGADA e aplicável, avaliada: acesa ou não, com o texto e o
 *  destino. Quem decide "aplicável" é quem chama (`lineApplies`). */
export function evaluateLine(key: OverviewAttentionLine, inputs: AttentionInputs): AttentionRow {
  const { summary, attention } = inputs;
  const threshold = attention[key].threshold;
  const window = attentionWindow(summary);
  const mentions = (): AttentionTarget => ({
    kind: "mentions",
    criteria: window ? lineCriteria(key, window) : null
  });

  const fromCount = (count: number | null | undefined): AttentionRow => {
    const copy = COUNT_COPY[key] as CountCopy;
    const label = ATTENTION_LINES.find(line => line.key === key)?.label ?? copy.noun;
    if (count === undefined) {
      return { key, lit: false, busy: false, title: label, detail: "Contando…", target: mentions() };
    }
    if (count === null) {
      return { key, lit: false, busy: false, title: label, detail: "Não foi possível contar agora", target: mentions() };
    }
    const lit = count >= threshold;
    return {
      key,
      lit,
      busy: false,
      title: `${formatInteger(count)} ${copy.noun}`,
      detail: lit ? copy.action : count ? belowLimit(formatInteger(threshold)) : copy.none,
      target: mentions()
    };
  };

  switch (key) {
    // As duas de antes saem do resumo, como os cartões acima delas.
    case "negative_mentions":
      return fromCount(summary ? summary.negative_count : null);
    case "unclassified_mentions":
      return fromCount(summary ? summary.unclassified_count : null);

    case "negative_share": {
      const classified = (summary?.polarity_counts ?? []).reduce((sum, entry) => sum + entry.count, 0);
      if (!summary || !classified) {
        return {
          key,
          lit: false,
          busy: false,
          title: "Proporção de negativas",
          detail: summary ? "Nenhuma menção classificada na janela" : "Indicador indisponível",
          target: mentions()
        };
      }
      // Sobre as CLASSIFICADAS, como o cartão de sentimento: menção sem
      // polaridade não é positiva nem negativa.
      const share = (summary.negative_count / classified) * 100;
      const lit = share >= threshold;
      return {
        key,
        lit,
        busy: false,
        title: `${formatInteger(Math.round(share))}% das classificadas são negativas`,
        detail: lit ? `Acima do limite de ${formatInteger(threshold)}%` : belowLimit(`${formatInteger(threshold)}%`),
        target: mentions()
      };
    }

    case "low_cena": {
      const average = summary?.average_cena ?? null;
      if (average === null) {
        return {
          key,
          lit: false,
          busy: false,
          title: "CENA médio",
          detail: summary ? "Sem menção com nota na janela" : "Indicador indisponível",
          target: { kind: "dashboard" }
        };
      }
      const lit = average < threshold;
      return {
        key,
        lit,
        busy: false,
        title: `CENA médio ${formatDecimal(average)}`,
        detail: lit ? `Abaixo do limite de ${formatDecimal(threshold)}` : `Acima do limite de alerta (${formatDecimal(threshold)})`,
        target: { kind: "dashboard" }
      };
    }

    case "low_cena_balance": {
      const balance = summary?.cena_balance ?? null;
      if (balance === null) {
        return {
          key,
          lit: false,
          busy: false,
          title: "Saldo reputacional",
          detail: summary ? "Sem menção com nota na janela" : "Indicador indisponível",
          target: { kind: "dashboard" }
        };
      }
      const lit = balance < threshold;
      return {
        key,
        lit,
        busy: false,
        title: `Saldo reputacional ${formatInteger(balance)}`,
        detail: lit ? `Abaixo do limite de ${formatInteger(threshold)}` : `Acima do limite de alerta (${formatInteger(threshold)})`,
        target: { kind: "dashboard" }
      };
    }

    case "upload_issues": {
      const { uploadIssues: issues, pendingUploads: pending } = inputs;
      const lit = issues >= threshold;
      return {
        key,
        lit,
        busy: !lit && pending > 0,
        title: issues
          ? `${formatInteger(issues)} uploads com pendências`
          : `${formatInteger(pending)} uploads em processamento`,
        detail: lit
          ? "Consultar erros e corrigir registros"
          : issues
            ? belowLimit(formatInteger(threshold))
            : pending
              ? "Acompanhar o pipeline de dados"
              : "Todos os uploads foram processados",
        target: { kind: "uploads" }
      };
    }

    case "stale_data": {
      const latest = summary?.latest_published_at ?? null;
      if (!latest) {
        return {
          key,
          lit: false,
          busy: false,
          title: "Último dado",
          detail: summary ? "Nenhuma menção recebida ainda" : "Indicador indisponível",
          target: { kind: "uploads" }
        };
      }
      const days = Math.max(0, dayNumber(inputs.today) - dayNumber(latest));
      const lit = days >= threshold;
      return {
        key,
        lit,
        busy: false,
        title: days === 0 ? "Último dado hoje" : days === 1 ? "Último dado ontem" : `Último dado há ${formatInteger(days)} dias`,
        detail: lit
          ? "Conferir se a clipadora continua enviando"
          : `Alerta a partir de ${formatInteger(threshold)} ${plural(threshold, "dia", "dias")}`,
        target: { kind: "uploads" }
      };
    }

    case "expiring_drafts": {
      const expiries = inputs.draftExpiries;
      const label = "Rascunhos de amostra";
      if (expiries === undefined) {
        return { key, lit: false, busy: false, title: label, detail: "Contando…", target: { kind: "drafts" } };
      }
      if (expiries === null) {
        return { key, lit: false, busy: false, title: label, detail: "Não foi possível contar agora", target: { kind: "drafts" } };
      }
      const limit = inputs.now + threshold * DAY_MS;
      const count = expiries.filter(value => {
        const moment = Date.parse(value);
        return moment > inputs.now && moment <= limit;
      }).length;
      const window = `${formatInteger(threshold)} ${plural(threshold, "dia", "dias")}`;
      return {
        key,
        lit: count > 0,
        busy: false,
        title: count
          ? `${formatInteger(count)} ${plural(count, "rascunho de amostra vence", "rascunhos de amostra vencem")} em até ${window}`
          : "Nenhum rascunho de amostra perto de vencer",
        detail: count ? "Salvar antes que vençam" : `Aviso com ${window} de antecedência`,
        target: { kind: "drafts" }
      };
    }

    default:
      return fromCount(inputs.counts[key]);
  }
}
